import Axios from 'axios';
import { getRefreshToken, hadTransientRefreshError, clearTokenCache } from './utils/spotify/login';
import { getFromLocalStorageWithExpiry } from './utils/localstorage';
import { logAxiosRequest } from './dev/requestLogger';
import { logAxiosResponse, logAxiosError } from './dev/responseLogger';
import { isVerboseLoggingEnabled } from './lib/debug/logging';
import { isSpotifyRequestAllowed } from './utils/spotify/requestWindow';

const shouldApplyCurfew = (url?: string | null): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url, resolvedBaseURL);
    const path = parsed.pathname || '';
    return matchesCurfewPath(path);
  } catch {
    const normalized = url.startsWith('/') ? url : `/${url}`;
    return matchesCurfewPath(normalized);
  }
};

const matchesCurfewPath = (path: string): boolean => {
  if (!path) return false;
  if (path.startsWith('/artists') || path.startsWith('/albums')) {
    return true;
  }
  if (path.startsWith('/browse')) {
    return (
      path.startsWith('/browse/categories') ||
      path.startsWith('/browse/featured-playlists') ||
      path.startsWith('/browse/new-releases')
    );
  }
  if (path.startsWith('/recommendations')) {
    return true;
  }
  if (path.startsWith('/me/top')) {
    return true;
  }
  if (path.startsWith('/me/tracks/contains')) {
    return true;
  }
  if (path.startsWith('/me/following/contains')) {
    return path.includes('type=user');
  }
  return false;
};

const DEFAULT_SPOTIFY_API_BASE = 'https://api.spotify.com/v1' as const;
const envBase =
  typeof process !== 'undefined' ? process.env.REACT_APP_SPOTIFY_API_BASE : undefined;

const inferLocalBase = () => {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (host === '127.0.0.1' || host === 'localhost') {
    return 'http://127.0.0.1:4000/spotify';
  }
  return null;
};

const resolvedBaseURL = envBase ?? inferLocalBase() ?? DEFAULT_SPOTIFY_API_BASE;
const verboseLoggingEnabled = isVerboseLoggingEnabled();

const access_token = getFromLocalStorageWithExpiry('access_token') as string;

const MAX_429_RETRIES = 3;
const RETRY_AFTER_FALLBACK_SECONDS = 5;
const TRANSIENT_RETRY_BASE_DELAY_MS = 500;

const axios = Axios.create({
  baseURL: resolvedBaseURL,
  headers: {},
});

if (verboseLoggingEnabled) {
  console.info('[CMF] axios baseURL', resolvedBaseURL);
}

if (access_token) {
  axios.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
}

// Request deduplication: prevent duplicate concurrent requests to the same endpoint
// Key format: METHOD:URL:serializedParams
const inFlightRequests = new Map<string, Promise<any>>();

const createRequestKey = (config: any): string => {
  const method = (config.method || 'get').toUpperCase();
  const url = config.url || '';
  // Serialize params in a stable way
  const params = config.params || {};
  const paramsStr = typeof params === 'string' 
    ? params 
    : Object.keys(params)
        .sort()
        .map(key => `${key}=${String(params[key])}`)
        .join('&');
  return `${method}:${url}:${paramsStr}`;
};

// Wrap axios.request to add deduplication
// Note: axios.get(), axios.post(), etc. all call axios.request() internally, so this wrapper catches all methods
const originalRequest = axios.request.bind(axios);
axios.request = function(config: any) {
  // Skip deduplication for retries and explicit skips
  if (config?._retry || config?._cmfSkipDeduplication || config?.__cmf429RetryCount) {
    return originalRequest(config);
  }

  // Guard against requests that might accidentally use the current page URL
  // This can happen if config.url is empty/undefined and axios falls back to current page
  const url = config?.url;
  if (url && typeof url === 'string' && typeof window !== 'undefined') {
    const currentPath = window.location.pathname;
    // Block if URL matches current page path (excluding query params) - this shouldn't happen
    if (url === currentPath || url === window.location.href) {
      console.error('[axios] Blocked request to current page URL:', url);
      return Promise.reject(new Error(`Invalid request URL: ${url} - cannot request current page`));
    }
  }

  const requestKey = createRequestKey(config);
  
  // Check if request already in-flight
  const existingRequest = inFlightRequests.get(requestKey);
  if (existingRequest) {
    // Request already in-flight, return the existing promise
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[axios] 🚫 Deduplicating duplicate request: ${requestKey}`);
    }
    return existingRequest;
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`[axios] ➕ New request: ${requestKey}`);
  }

  // Create new request promise and set it IMMEDIATELY to catch concurrent requests
  // This must happen synchronously before any async operations
  const requestPromise = originalRequest(config);
  
  // Set in map immediately (synchronously) to prevent race conditions
  inFlightRequests.set(requestKey, requestPromise);

  // Clean up after request completes
  requestPromise.finally(() => {
    // Clear the key after a delay to allow quick re-requests to share
    setTimeout(() => {
      // Only delete if it's still our promise (in case a new one was set)
      if (inFlightRequests.get(requestKey) === requestPromise) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[axios] 🗑️ Clearing request cache: ${requestKey}`);
        }
        inFlightRequests.delete(requestKey);
      }
    }, 500);
  });

  return requestPromise;
};

axios.interceptors.request.use((config) => {
  if (shouldApplyCurfew(config.url)) {
    const allowed = isSpotifyRequestAllowed({
      reason: config.url ?? 'axios-request',
    });
    if (!allowed) {
      const error = new Error('Spotify requests are disabled during the current curfew window.');
      return Promise.reject(error);
    }
  }
  if (verboseLoggingEnabled) {
    logAxiosRequest(config);
  }
  return config;
});

if (verboseLoggingEnabled) {
  axios.interceptors.response.use(logAxiosResponse, logAxiosError);
}

const parseRetryAfterMs = (headers: Record<string, any> | undefined): number | null => {
  if (!headers) return null;
  const raw = headers['retry-after'] ?? headers['Retry-After'];
  if (!raw) return null;

  const numeric = Number(raw);
  if (Number.isFinite(numeric) && numeric >= 0) {
    return numeric * 1000;
  }

  const parsedDate = Date.parse(raw);
  if (!Number.isNaN(parsedDate)) {
    const delta = parsedDate - Date.now();
    return delta > 0 ? delta : null;
  }

  return null;
};

const computeRetryDelayMs = (retryCount: number, retryAfterMs: number | null): number => {
  if (typeof retryAfterMs === 'number' && retryAfterMs > 0) {
    return Math.min(retryAfterMs, 60_000);
  }
  const base = TRANSIENT_RETRY_BASE_DELAY_MS * Math.pow(2, retryCount);
  const jitter = Math.random() * TRANSIENT_RETRY_BASE_DELAY_MS;
  return Math.min(60_000, base + jitter);
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 429) {
      const retryCount = error.config?.__cmf429RetryCount ?? 0;
      if (retryCount >= MAX_429_RETRIES) {
        return Promise.reject(error);
      }

      const retryAfterMs = parseRetryAfterMs(error.response?.headers);
      const delayMs = computeRetryDelayMs(retryCount, retryAfterMs ?? RETRY_AFTER_FALLBACK_SECONDS * 1000);

      if (process.env.NODE_ENV === 'development') {
        console.warn('[axios] Spotify 429 received; retrying with backoff', {
          url: error.config?.url,
          retryCount: retryCount + 1,
          retryAfterMs,
          delayMs: Math.round(delayMs),
        });
      }

      const retryConfig = {
        ...error.config,
        __cmf429RetryCount: retryCount + 1,
        _cmfSkipDeduplication: true,
      };

      return new Promise((resolve) => setTimeout(resolve, delayMs)).then(() => originalRequest(retryConfig));
    }

    if (error.response?.status === 401) {
      // Clear cache since the cached token was rejected
      clearTokenCache();
      const currentToken = getFromLocalStorageWithExpiry('access_token');
      const refreshToken =
        localStorage.getItem('refresh_token') ?? localStorage.getItem('spotify_refresh_token');
      if (!currentToken && !refreshToken) {
        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        if (path.startsWith('/how-it-works')) {
          if (process.env.NODE_ENV === 'development') {
            console.info('[axios] Skipping auth refresh on /how-it-works route');
          }
          return Promise.reject(error);
        }
      }
      
      return getRefreshToken()
        .then((token) => {
          if (!token) {
            // Only clear tokens if it's NOT a transient error
            // Transient errors (500, network issues) should preserve tokens for retry
            if (!hadTransientRefreshError()) {
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('spotify_refresh_token');
              localStorage.removeItem('access_token');
              localStorage.removeItem('spotify_access_token');
              localStorage.removeItem('public_access_token');
            }
            return Promise.reject(error);
          }
          axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
          if (error.config?.headers) {
            error.config.headers['Authorization'] = 'Bearer ' + token;
          }
          // Mark retry to skip deduplication
          const retryConfig = { ...error.config, _retry: true };
          return originalRequest(retryConfig);
        })
        .catch(() => {
          // Only clear tokens if it's NOT a transient error
          if (!hadTransientRefreshError()) {
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('spotify_refresh_token');
            localStorage.removeItem('access_token');
            localStorage.removeItem('spotify_access_token');
            localStorage.removeItem('public_access_token');
          }
          return Promise.reject(error);
        });
    }
    return Promise.reject(error);
  }
);

export default axios;
